const DEFAULT_MAX_DEPTH = 3;
const DEFAULT_MAX_CHARS = 2048;

/** A JSON-safe stand-in for a value dropped past the depth limit. */
const DEPTH_LIMIT_PLACEHOLDER = '[Truncated]';

/**
 * Returns a plain JSON-safe copy of `value`, defaulting to `{ maxDepth: 3, maxChars: 2048 }`.
 *
 * Bounds:
 * - Strings are truncated to `maxChars`, counted per string rather than across the copy.
 * - Objects and arrays nested deeper than `maxDepth` become {@link DEPTH_LIMIT_PLACEHOLDER}.
 * - Functions, symbols, bigints and circular references are dropped.
 * - A `Date` becomes its ISO string.
 * - A `Map` becomes a plain object under its stringified keys; a `Set` becomes an array of its values.
 * - Two `Map` keys that stringify alike therefore keep one entry.
 */
export function boundContext(value: unknown, options?: { maxDepth?: number; maxChars?: number }): unknown {
  const maxDepth = options?.maxDepth ?? DEFAULT_MAX_DEPTH;
  const maxChars = options?.maxChars ?? DEFAULT_MAX_CHARS;
  return bound(value, maxDepth, maxChars, new Set());
}

function bound(value: unknown, depthRemaining: number, maxChars: number, ancestors: Set<object>): unknown {
  if (typeof value === 'string') {
    return value.length > maxChars ? value.slice(0, maxChars) : value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (value === null || typeof value !== 'object') {
    return typeof value === 'function' || typeof value === 'symbol' || typeof value === 'bigint' ? undefined : value;
  }

  if (ancestors.has(value)) {
    return undefined;
  }

  if (depthRemaining <= 0) {
    return DEPTH_LIMIT_PLACEHOLDER;
  }

  ancestors.add(value);
  try {
    // A pair array puts a sensitive key out of reach of key-based scrubbing.
    if (value instanceof Map) {
      return bound(Object.fromEntries(value), depthRemaining, maxChars, ancestors);
    }

    if (value instanceof Set) {
      return bound([...value], depthRemaining, maxChars, ancestors);
    }

    if (Array.isArray(value)) {
      return value
        .map(item => bound(item, depthRemaining - 1, maxChars, ancestors))
        .filter(item => item !== undefined);
    }

    const result: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      const boundItem = bound(item, depthRemaining - 1, maxChars, ancestors);
      if (boundItem !== undefined) {
        result[key] = boundItem;
      }
    }
    return result;
  } finally {
    ancestors.delete(value);
  }
}
